import {
  AdproductsMoodysReportType,
  type CustomerAnalyticsMoodysAskingRentVacancyDto,
  type CustomerAnalyticsMoodysReportDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { Image, Text, View } from '@react-pdf/renderer';
import { styles } from './pdfStyles';
import {
  MarketTrendsColumn,
  MarketTrendsHr,
  MarketTrendsPageWrapper,
  MarketTrendsTextHeader,
} from './MarketTrendsComponents';
import { getRowStyle } from './pdfImageUtils';
import { formatCurrency } from '../formatCurrency';

const currentYear = new Date().getFullYear();

const KeyPerformanceBox = ({
  title,
  subTitle,
  value,
  change,
  lastQ,
  unit,
  changeResult,
}: {
  title: string;
  subTitle?: string;
  value: string;
  change?: number;
  lastQ: string;
  unit?: string;
  changeResult: 'upGood' | 'downGood';
}) => {
  const formattedChange = Math.round((change || 0) * 100 + Number.EPSILON) / 100;

  let changeCopy = 'unchanged';
  let resultColor = '';
  if (formattedChange < 0) {
    resultColor = changeResult === 'upGood' ? 'green' : 'red';
    changeCopy = 'down';
  } else if (formattedChange > 0) {
    resultColor = changeResult === 'upGood' ? 'red' : 'green';
    changeCopy = 'up';
  }

  return (
    <View style={{ padding: 5, border: '1px sold grey', borderRadius: 5, marginRight: 10 }}>
      <Text
        style={{
          fontFamily: 'OpenSansBold',
          fontSize: 10,
        }}
      >
        {title}
      </Text>
      {subTitle && (
        <Text
          style={{
            fontFamily: 'OpenSans',
            fontSize: 8,
            color: '#A7A6AB',
          }}
        >
          {subTitle}
        </Text>
      )}
      <Text style={{ marginTop: 5, marginBottom: 5, color: '#0D4599', fontFamily: 'OpenSansBold', fontSize: 20 }}>
        {value}
      </Text>

      {change !== undefined && (
        <Text style={{ fontSize: 10 }}>
          <Text style={{ color: resultColor }}>{changeCopy}</Text>
          {`${formattedChange !== 0 ? ` ${Math.abs(formattedChange)}${unit}` : ''} from last ${lastQ}`}
        </Text>
      )}
    </View>
  );
};

export const OverviewPage = ({
  report,
  rentTrendsBarChartSrc,
  reportsInRange,
  showKeyPerformanceIndicators = true,
}: {
  report: CustomerAnalyticsMoodysReportDto;
  rentTrendsBarChartSrc?: string;
  reportsInRange: Array<CustomerAnalyticsMoodysAskingRentVacancyDto>;
  showKeyPerformanceIndicators?: boolean;
}) => {
  const reducer: Record<string, CustomerAnalyticsMoodysAskingRentVacancyDto> = {};
  const marketRentsByYear = report.askingRentVacancyTrendsReport.market.reduce((accumulator, currentValue) => {
    accumulator[currentValue.year] = currentValue;
    return accumulator;
  }, reducer);

  const inRangeInventory = reportsInRange.sort((a, b) => a.year - b.year).filter((item) => item.year <= currentYear);
  const hasSubmarketData = report.reportType === AdproductsMoodysReportType.SUBMARKET;
  const totalCols = hasSubmarketData ? 3 : 2;
  const colWidth = 100 / totalCols;
  const showRentTrends = Boolean(rentTrendsBarChartSrc?.length);

  const effectiveRent = report.keyPerformanceIndicatorsReport.effectiveRent;
  const getAverageAskingRent = () =>
    report.askingRentVacancyTrendsReport.market.reduce((sum, n) => sum + n.askingRent, 0) /
    report.askingRentVacancyTrendsReport.market.length;
  const rentMetric = formatCurrency(effectiveRent ? effectiveRent : getAverageAskingRent());
  const rentMetricTitle = effectiveRent ? 'Effective Rent' : 'Asking Rent';
  return (
    <MarketTrendsPageWrapper quarter={report.dataAsOfQuarter} year={report.dataAsOfYear}>
      <View style={styles.containerBase}>
        {showKeyPerformanceIndicators && (
          <>
            <View style={{ paddingBottom: 5 }}>
              <Text style={styles.subHeading}>Key Performance Indicators</Text>
            </View>
            <MarketTrendsHr />
            <View style={{ display: 'flex', flexDirection: 'row', marginVertical: '20px' }}>
              {Boolean(report.keyPerformanceIndicatorsReport.effectiveRent) && (
                <KeyPerformanceBox
                  title={rentMetricTitle}
                  subTitle="PER UNIT"
                  value={rentMetric}
                  change={effectiveRent ? report.keyPerformanceIndicatorsReport.effectiveRentChange * 100 : undefined}
                  lastQ={report.keyPerformanceIndicatorsReport.previousQuarterYear}
                  changeResult="downGood"
                  unit="%"
                />
              )}
              <KeyPerformanceBox
                title="Vacancy Rate"
                subTitle="PER UNIT"
                value={`${
                  Math.round(report.keyPerformanceIndicatorsReport.vacancyRate * 100 * 10 + Number.EPSILON) / 10
                }%`}
                change={report.keyPerformanceIndicatorsReport.vacancyRateBpsChange / 10}
                lastQ={report.keyPerformanceIndicatorsReport.previousQuarterYear}
                unit="%"
                changeResult="upGood"
              />
            </View>
          </>
        )}
        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center' }}>
          <View>
            <Image src={rentTrendsBarChartSrc} />
          </View>
        </View>
        {showRentTrends && (
          <>
            <MarketTrendsTextHeader headerText="Asking Rent Trends" paddingBottom={20} />
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <MarketTrendsColumn width={colWidth} />
                {hasSubmarketData && (
                  <MarketTrendsColumn width={colWidth}>
                    <Text>{report.submarketName}</Text>
                  </MarketTrendsColumn>
                )}
                <MarketTrendsColumn width={colWidth}>
                  <Text>{report.marketName}</Text>
                </MarketTrendsColumn>
              </View>
              <View style={styles.tableHeader}>
                <MarketTrendsColumn width={colWidth}>
                  <Text>Year</Text>
                </MarketTrendsColumn>
                {hasSubmarketData && (
                  <MarketTrendsColumn width={colWidth}>
                    <Text>Asking Rent</Text>
                    <Text style={styles.tableSubHeading}>Per Unit</Text>
                  </MarketTrendsColumn>
                )}
                <MarketTrendsColumn width={colWidth}>
                  <Text>Asking Rent</Text>
                  <Text style={styles.tableSubHeading}>Per Unit</Text>
                </MarketTrendsColumn>
              </View>
              {inRangeInventory.map((marketInventory, i) => (
                <View style={getRowStyle(i)} key={marketInventory.year + marketInventory.askingRent}>
                  <MarketTrendsColumn width={colWidth}>
                    <Text> {marketInventory.year}</Text>
                  </MarketTrendsColumn>
                  {hasSubmarketData && (
                    <MarketTrendsColumn width={colWidth}>
                      <Text> {formatCurrency(marketInventory.askingRent)}</Text>
                    </MarketTrendsColumn>
                  )}
                  <MarketTrendsColumn width={colWidth}>
                    <Text>{formatCurrency(marketRentsByYear[marketInventory.year].askingRent)}</Text>
                  </MarketTrendsColumn>
                </View>
              ))}
            </View>
          </>
        )}
      </View>
    </MarketTrendsPageWrapper>
  );
};
